import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowLeft, Wallet, Undo2, Gavel, RefreshCw } from "lucide-react";
import { fetchAdminMe, fetchAdminEscrows, adminEscrowAction } from "@/lib/api";
import { fmtPrice } from "@/lib/utils";
import { ESCROW_STATUS } from "@/pages/EscrowStatusPage";

const TABS = [
  { key: "a_verser", label: "A verser" },
  { key: "disputed", label: "Litiges" },
  { key: "paid", label: "Fonds bloques" },
  { key: "shipped", label: "Livres" },
  { key: "refunded", label: "A rembourser" },
  { key: "paid_out", label: "Terminees" },
  { key: "", label: "Toutes" },
];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function AdminEscrowsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("a_verser");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const load = (t = tab) => fetchAdminEscrows(t || undefined).then(setData).catch(() => toast.error("Erreur de chargement"));
  useEffect(() => { fetchAdminMe().then(() => load()).catch(() => navigate("/", { replace: true })); }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (ref, action, body) => {
    setBusy(ref);
    try { await adminEscrowAction(ref, action, body); toast.success(`${ref} : ${action} OK`); setNote(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Echec"); }
    finally { setBusy(""); }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-escrows-page">
      <Link to="/admin" className="text-sm inline-flex items-center gap-1 text-[var(--ink-soft)]"><ArrowLeft size={14} /> Bastion admin</Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mt-2">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold inline-flex items-center gap-2"><ShieldCheck className="text-emerald-600" /> Paiements sequestres</h1>
        <div className="flex items-center gap-3">
          {data && <span className="text-sm font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full" data-testid="escrow-held-total">Fonds detenus : {fmtPrice(data.held_total)}</span>}
          <button onClick={() => load()} className="chip inline-flex items-center gap-1"><RefreshCw size={12} /> Rafraichir</button>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap mt-5" data-testid="escrow-tabs">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`chip ${tab === t.key ? "active" : ""}`} data-testid={`escrow-tab-${t.key || "all"}`}>
            {t.label}{data?.counts?.[t.key] != null && t.key ? ` (${data.counts[t.key]})` : ""}
          </button>
        ))}
      </div>
      {!data ? <Loader2 className="animate-spin mt-8 text-emerald-600" /> : data.items.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)] mt-6" data-testid="escrows-admin-empty">Aucune transaction dans cet onglet.</p>
      ) : (
        <div className="overflow-x-auto mt-5 glass-card p-4">
          <table className="w-full text-sm" data-testid="escrows-admin-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-soft)] border-b border-[var(--line)]">
              <th className="py-2 pr-3">Ref</th><th className="py-2 pr-3">Annonce</th><th className="py-2 pr-3">Acheteur</th><th className="py-2 pr-3">Vendeur</th><th className="py-2 pr-3">Montant</th><th className="py-2 pr-3">A verser</th><th className="py-2 pr-3">Statut</th><th className="py-2 pr-3">MAJ</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {data.items.map((e) => (
                <tr key={e.ref} className="border-b border-[var(--line)]/60 align-top" data-testid={`escrow-admin-row-${e.ref}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{e.ref}</td>
                  <td className="py-2 pr-3 max-w-[180px] truncate">{e.annonce_title}</td>
                  <td className="py-2 pr-3 text-xs">{e.buyer_name}<br />{e.buyer_phone}</td>
                  <td className="py-2 pr-3 text-xs">{e.seller_name}<br /><b>{e.seller_phone}</b></td>
                  <td className="py-2 pr-3">{fmtPrice(e.amount)}</td>
                  <td className="py-2 pr-3 font-bold">{fmtPrice(e.seller_amount)}</td>
                  <td className="py-2 pr-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${(ESCROW_STATUS[e.status] || {}).cls || ""}`}>{(ESCROW_STATUS[e.status] || {}).label || e.status}</span>
                    {e.dispute && <div className="text-xs text-red-700 mt-1">{e.dispute_reasons?.[e.dispute.reason] || e.dispute.reason}{e.dispute.details ? ` — ${e.dispute.details}` : ""}</div>}
                  </td>
                  <td className="py-2 pr-3 text-xs">{fmtDate(e.updated_at)}</td>
                  <td className="py-2 text-right space-y-1 min-w-[170px]">
                    {e.status === "released" && e.payout?.status === "a_verser" && (
                      <button onClick={() => act(e.ref, "payout")} disabled={busy === e.ref} className="btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`escrow-payout-${e.ref}`}><Wallet size={12} /> Marquer verse</button>
                    )}
                    {e.status === "disputed" && (
                      <div className="space-y-1">
                        <input value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Note de decision" className="!py-1 !px-2 text-xs" data-testid={`escrow-note-${e.ref}`} />
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => act(e.ref, "resolve", { decision: "release", note })} disabled={busy === e.ref} className="btn-green !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`escrow-release-${e.ref}`}><Gavel size={12} /> Payer vendeur</button>
                          <button onClick={() => act(e.ref, "resolve", { decision: "refund", note })} disabled={busy === e.ref} className="btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid={`escrow-refund-${e.ref}`}><Undo2 size={12} /> Rembourser</button>
                        </div>
                      </div>
                    )}
                    {e.status === "refunded" && e.refund?.status === "a_traiter" && (
                      <button onClick={() => act(e.ref, "refund-done")} disabled={busy === e.ref} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid={`escrow-refund-done-${e.ref}`}>Remboursement effectue</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
